import { useEffect, useState } from 'react';
import { ProvinceFormattedData } from '../../features/danh-muc/khac/danh-muc-tinh-thanh/schemas';
import { TinhThanh, TinhThanhResponse } from '@/types/schemas';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UseTinhThanhReturn {
  provinces: TinhThanh[];
  isLoading: boolean;
  addProvince: (newProvince: ProvinceFormattedData) => Promise<void>;
  updateProvince: (uuid: string, updatedProvince: ProvinceFormattedData) => Promise<void>;
  deleteProvince: (uuid: string) => Promise<void>;
  refreshProvinces: () => Promise<void>;
}

export const useTinhThanh = (initialProvinces: TinhThanh[] = []): UseTinhThanhReturn => {
  const [provinces, setProvinces] = useState<TinhThanh[]>(initialProvinces);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchProvinces = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    const response = await api.get<TinhThanhResponse>(`/entities/${entity.slug}/erp/provinces/`);

    const mappedData: TinhThanh[] = response.data.results;

    setProvinces(mappedData);
    setIsLoading(false);
  };

  const addProvince = async (newProvince: ProvinceFormattedData) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    const response = await api.post(`/entities/${entity.slug}/erp/provinces/`, {
      ma_tinh: newProvince.ma_tinh,
      ten_tinh: newProvince.ten_tinh,
      ten_tinh2: newProvince.ten_tinh2 || null,
      status: newProvince.status || '1'
    });

    const addedProvince: TinhThanh = response.data;

    setProvinces(prev => [...prev, addedProvince]);
    setIsLoading(false);
  };

  const updateProvince = async (uuid: string, updatedProvince: ProvinceFormattedData) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    const response = await api.patch(`/entities/${entity.slug}/erp/provinces/${uuid}/`, {
      ma_tinh: updatedProvince.ma_tinh,
      ten_tinh: updatedProvince.ten_tinh,
      ten_tinh2: updatedProvince.ten_tinh2 || null,
      status: updatedProvince.status || '1'
    });

    // Update the province in the list
    const updatedProvinceData: TinhThanh = response.data;

    setProvinces(prev =>
      prev.map(province => (province.uuid === updatedProvinceData.uuid ? updatedProvinceData : province))
    );
    setIsLoading(false);
  };

  const deleteProvince = async (uuid: string) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    await api.delete(`/entities/${entity.slug}/erp/provinces/${uuid}/`);

    // Remove the province from the list
    setProvinces(prev => prev.filter(province => province.uuid !== uuid));
    setIsLoading(false);
  };

  useEffect(() => {
    fetchProvinces();
  }, [entity?.slug]);

  return {
    provinces,
    isLoading,
    addProvince,
    updateProvince,
    deleteProvince,
    refreshProvinces: fetchProvinces
  };
};
